/**
 * Adapter: convertono i tipi GraphQL (snake_case, AppSync) nei tipi che i
 * componenti Home già consumano (camelCase, `types.ts`).
 *
 * Mantenere QUI le trasformazioni rende i componenti UI agnostici alla fonte:
 * domani se il backend cambia campo, modifichiamo solo questi adapter.
 */

import dayjs from 'dayjs'
import type { InvoiceStatus } from '@/components/ui/badge'
import { formatBackendPeriodRange } from '@/lib/dates'
import type { Bolletta, Contratto } from '@/lib/graphql/types'
import type { AutoletturaWindow, Invoice } from './types'

const STATUS_FALLBACK: InvoiceStatus = 'da pagare'

/** Normalizza lo stato bolletta backend → set chiuso dell'UI. */
function normalizeStatus(stato?: string | null): InvoiceStatus {
  if (!stato) return STATUS_FALLBACK
  const s = stato.toLowerCase()
  if (s.includes('pagat')) return 'pagata'
  if (s.includes('scadenz')) return 'in scadenza'
  if (s.includes('scadut')) return 'scaduta'
  if (s.includes('rateizz')) return 'rateizzata'
  if (s.includes('stornat')) return 'stornata'
  return 'da pagare'
}

/** Mappa il `metodo_pagamento` backend (string libero) al set UI. */
function normalizePaymentMethod(raw: string | undefined): Invoice['paymentMethod'] {
  if (!raw) return 'Bonifico'
  const s = raw.toLowerCase()
  if (s.includes('sdd') || s.includes('rid') || s.includes('addebito')) return 'SDD'
  if (s.includes('bollettino')) return 'Bollettino'
  if (s.includes('prepag')) return 'Prepagata'
  if (s.includes('sardex')) return 'Sardex'
  return 'Bonifico'
}

export function adaptInvoice(
  b: Bolletta | undefined,
  contract: Contratto | null,
): Invoice | null {
  if (!b) return null
  return {
    id: b.codice,
    code: b.codice,
    periodLabel: formatBackendPeriodRange(b.dal, b.al, b.data_emissione),
    amount: Number(b.imponibile) || 0,
    currency: 'EUR',
    status: normalizeStatus(b.stato),
    // `dueDate` resta in formato backend (DD/MM/YYYY): la UI lo passa al
    // formatter `formatDateShort` che ora sa parsarlo.
    dueDate: b.data_scadenza,
    consumptionKwh: Math.round(Number(b.consumo) || 0),
    paymentMethod: normalizePaymentMethod(contract?.metodo_pagamento),
  }
}

/**
 * Deriva la finestra autolettura dagli `alerts` del contratto attivo.
 * Replicato dal mobile: cerca un alert con `tipo` che contiene "autolettura".
 * Nel nostro schema reale `ContractAlert` ha solo `tipo` + `stato` (niente
 * `scadenza`); per la data esatta servirà un'altra query dedicata in futuro.
 */
export function adaptAutolettura(contract: Contratto): AutoletturaWindow {
  const alert = contract.alerts?.find((a) => /autolettur/i.test(a.tipo))
  if (!alert) {
    return { mode: 'closed' }
  }

  // ARERA non impone una finestra legale di autolettura per l'energia elettrica
  // (a differenza del gas). Convenzione commerciale Enermed: la finestra di
  // invio comprende **ultimi 2 giorni del mese in corso + primi 2 del mese
  // successivo**. Inoltre 1 giorno prima dell'apertura (terzultimo del mese)
  // mostriamo una "preview" come heads-up.
  //
  // Esempi (mese settembre, 30 giorni):
  //   28 set → preview (apre domani, 29 set), closesOn = 2 ott
  //   29 set → open (penultimo)
  //   30 set → open (ultimo)
  //   1 ott  → open
  //   2 ott  → open (ultimo giorno utile)
  //   3 ott  → closed (riaprirà il 30 ott)
  //   29 ott → preview
  const today = dayjs().startOf('day')
  const dayOfMonth = today.date()
  const endOfMonth = today.endOf('month').date()

  const inWindow =
    dayOfMonth >= endOfMonth - 1 /* penultimo o ultimo del mese */ ||
    dayOfMonth <= 2 /* primo o secondo del mese */

  // Preview = il giorno terzultimo del mese (es. il 29 in un mese da 31 gg,
  // il 28 in un mese da 30, ecc.). Solo se non siamo già in finestra.
  const isPreview = !inWindow && dayOfMonth === endOfMonth - 2

  // `closesOn` = giorno 2 del mese in cui la finestra corrente/prossima si chiude.
  // `opensOn` = penultimo giorno del mese in cui la finestra apre.
  let closesOn = today
  let opensOn = today
  if (dayOfMonth >= endOfMonth - 1) {
    // Già negli ultimi 2 giorni del mese
    closesOn = today.add(1, 'month').startOf('month').add(1, 'day')
    opensOn = today.endOf('month').subtract(1, 'day')
  } else if (dayOfMonth <= 2) {
    // Nei primi 2 giorni del mese
    closesOn = today.startOf('month').add(1, 'day')
    opensOn = today.subtract(1, 'month').endOf('month').subtract(1, 'day')
  } else if (isPreview) {
    // Terzultimo: finestra si apre domani, chiude al 2 del prossimo mese
    closesOn = today.add(1, 'month').startOf('month').add(1, 'day')
    opensOn = today.add(1, 'day')
  } else {
    // Fuori finestra: prossima apertura = penultimo del mese corrente
    closesOn = today.add(1, 'month').startOf('month').add(1, 'day')
    opensOn = today.endOf('month').subtract(1, 'day')
  }

  const backendOpen = !alert.stato || /aperta|open|attiv/i.test(alert.stato)

  let mode: AutoletturaWindow['mode'] = 'closed'
  if (backendOpen && inWindow) mode = 'open'
  else if (backendOpen && isPreview) mode = 'preview'

  return {
    mode,
    closesOn: closesOn.format('YYYY-MM-DD'),
    opensOn: opensOn.format('YYYY-MM-DD'),
    podDisplay: contract.pod.etichetta?.trim() || contract.pod.indirizzo?.trim() || undefined,
    podCode: contract.pod.pod?.trim() || undefined,
  }
}

